import { Mail } from 'lucide-react';
import { decodeHtmlEntities } from '../../modules/email/utils/html-decode';

interface EmailThread {
  id: string;
  gmail_thread_id: string;
  subject: string;
  from_addr: string;
  snippet: string;
  received_at: string;
  is_read: boolean;
  message_count: number;
  is_first_contact?: boolean;
  classification?: string;
}

interface EmailThreadListProps {
  threads: EmailThread[];
  selectedThreadId: string | null;
  onThreadSelect: (thread: EmailThread) => void;
  loading?: boolean;
}

function getClassificationBadge(classification?: string) {
  if (!classification || classification === 'other') return null;

  const labels: Record<string, { label: string; color: string }> = {
    collab: { label: 'Collaboration', color: 'bg-blue-100 text-blue-800' },
    gift: { label: 'Gift', color: 'bg-purple-100 text-purple-800' },
    event: { label: 'Événement', color: 'bg-green-100 text-green-800' },
    admin: { label: 'Admin', color: 'bg-gray-100 text-gray-800' },
    spam: { label: 'Spam', color: 'bg-red-100 text-red-800' },
  };

  const badge = labels[classification];
  if (!badge) return null;

  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
      {badge.label}
    </span>
  );
}

function formatTimestamp(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return `Il y a ${diffDays}j`;

  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function extractSenderName(fromAddr: string): string {
  const match = fromAddr.match(/^(.+?)\s*<.+>$/);
  if (match) return match[1].trim();
  return fromAddr.split('@')[0];
}

export default function EmailThreadList({
  threads,
  selectedThreadId,
  onThreadSelect,
  loading = false,
}: EmailThreadListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <Mail className="h-12 w-12 mb-3 text-gray-300" />
        <p className="text-sm">Aucun e-mail</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {threads.map((thread) => {
        const isSelected = thread.gmail_thread_id === selectedThreadId;
        const senderName = extractSenderName(thread.from_addr);

        return (
          <div
            key={thread.id}
            onClick={() => onThreadSelect(thread)}
            className={`
              p-4 border-b cursor-pointer transition-colors
              ${isSelected ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'}
              ${!thread.is_read ? 'bg-blue-50/30' : ''}
            `}
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm truncate ${!thread.is_read ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'}`}>
                    {decodeHtmlEntities(senderName)}
                  </span>
                  {getClassificationBadge(thread.classification)}
                </div>
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {formatTimestamp(thread.received_at)}
              </span>
            </div>

            <div className={`text-sm mb-1 truncate ${!thread.is_read ? 'font-semibold text-gray-900' : 'text-gray-900'}`}>
              {decodeHtmlEntities(thread.subject) || '(Sans objet)'}
            </div>

            <div className="text-sm text-gray-600 truncate">
              {decodeHtmlEntities(thread.snippet)}
            </div>

            <div className="flex items-center justify-between mt-2">
              {!thread.is_read && (
                <div className="inline-block w-2 h-2 bg-blue-600 rounded-full"></div>
              )}
              {thread.message_count > 1 && (
                <span className="text-xs text-gray-500 ml-auto">
                  {thread.message_count} messages
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
